/**
 * Accordion support: Start/End sections and optional .accordion-support class.
 * - Finds [data-accordion-start] and next [data-accordion-end], groups sections between them
 *   into one accordion (mobile-only; CSS shows stacked on desktop).
 * - Optionally converts .accordion-support elements into single collapsible blocks.
 */

const CHEVRON_SVG =
  '<span class="inline-block w-5 h-5 align-middle svg-scale ml-auto transform origin-center transition" :class="{ \'rotate-180\': open }"><svg class="theme-icon w-full h-full" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 12L23.2826 36.0527C23.3724 36.1542 23.4826 36.2354 23.6062 36.291C23.7297 36.3467 23.8636 36.3755 23.999 36.3755C24.1345 36.3755 24.2684 36.3467 24.3919 36.291C24.5154 36.2354 24.6257 36.1542 24.7155 36.0527L46 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';

function getSectionWrapper(el) {
  return el.closest('[id^="shopify-section-"]');
}

function findEndSectionWrapper(startWrapper) {
  let next = startWrapper.nextElementSibling;
  while (next) {
    if (next.querySelector('[data-accordion-end]')) return next;
    next = next.nextElementSibling;
  }
  return null;
}

function collectSectionWrappersBetween(startWrapper, endWrapper) {
  const sections = [];
  let el = startWrapper.nextElementSibling;
  while (el && el !== endWrapper) {
    if (el.id && el.id.startsWith('shopify-section-')) sections.push(el);
    el = el.nextElementSibling;
  }
  return sections;
}

function parseLabels(marker) {
  const raw = marker.getAttribute('data-accordion-labels');
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function buildAccordionGroup(startMarker, sectionWrappers, labels, expandedIndex) {
  const expanded = Math.max(0, Math.min(expandedIndex, sectionWrappers.length - 1));
  const wrapper = document.createElement('div');
  wrapper.className = 'accordion-group border-scheme-text border-b-2';
  wrapper.setAttribute('data-accordion-mobile-only', '');
  wrapper.setAttribute('data-accordion-built', '');

  sectionWrappers.forEach((sectionEl, index) => {
    const label = labels[index] != null ? labels[index] : `Item ${index + 1}`;
    const panelId = `accordion-panel-${index}-${Math.random().toString(36).slice(2, 9)}`;
    const isOpen = index === expanded;

    const item = document.createElement('div');
    item.className = 'accordion-item border-scheme-text border-t-2';
    item.setAttribute('x-data', `{ open: ${isOpen} }`);
    item.setAttribute('x-cloak', '');

    const header = document.createElement('button');
    header.type = 'button';
    header.className =
      'accordion-header w-full flex items-center py-4 font-heading heading-secondary cursor-pointer text-left text-scheme-text';
    header.setAttribute('@click', 'open = !open');
    header.setAttribute(':aria-expanded', 'open');
    header.setAttribute('aria-controls', panelId);
    header.innerHTML = `<span class="break-words flex-1">${escapeHtml(label)}</span>${CHEVRON_SVG}`;

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'accordion-panel';
    panel.setAttribute('x-show', 'open');
    panel.setAttribute('x-transition:enter', 'transition ease-out duration-200');
    panel.setAttribute('x-transition:enter-start', 'opacity-0');
    panel.setAttribute('x-transition:enter-end', 'opacity-100');
    panel.setAttribute('x-transition:leave', 'transition ease-in duration-150');
    panel.setAttribute('x-transition:leave-start', 'opacity-100');
    panel.setAttribute('x-transition:leave-end', 'opacity-0');
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', header.id || '');

    while (sectionEl.firstChild) panel.appendChild(sectionEl.firstChild);
    if (sectionEl.id) panel.dataset.sectionId = sectionEl.id;

    item.appendChild(header);
    item.appendChild(panel);
    wrapper.appendChild(item);
  });

  return wrapper;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function runStartEndAccordions(root = document) {
  const startMarkers = root.querySelectorAll('[data-accordion-start]');
  startMarkers.forEach((marker) => {
    const startWrapper = getSectionWrapper(marker);
    if (!startWrapper) return;
    const endWrapper = findEndSectionWrapper(startWrapper);
    if (!endWrapper) return;

    const sectionWrappers = collectSectionWrappersBetween(startWrapper, endWrapper);
    if (sectionWrappers.length === 0) return;

    const labels = parseLabels(marker);
    const expandedIndex = parseInt(marker.getAttribute('data-accordion-expanded-index') || '0', 10) || 0;

    const accordionWrapper = buildAccordionGroup(marker, sectionWrappers, labels, expandedIndex);

    const parent = startWrapper.parentNode;
    parent.insertBefore(accordionWrapper, startWrapper);
    let toRemove = startWrapper;
    while (toRemove) {
      const next = toRemove.nextElementSibling;
      toRemove.remove();
      if (toRemove === endWrapper) break;
      toRemove = next;
    }

    if (typeof Alpine !== 'undefined' && Alpine.initTree) {
      Alpine.initTree(accordionWrapper);
    }
  });
}

function runAccordionSupportClass(root = document) {
  root.querySelectorAll('.accordion-support:not([data-accordion-initialized])').forEach((el) => {
    el.setAttribute('data-accordion-initialized', '');
    const label =
      el.getAttribute('data-accordion-label') ||
      (el.querySelector('h1, h2, h3, h4, h5, h6') && el.querySelector('h1, h2, h3, h4, h5, h6').textContent.trim()) ||
      'Toggle';
    const expanded = el.getAttribute('data-accordion-expanded') !== 'false';
    const panelId = `accordion-support-${Math.random().toString(36).slice(2, 11)}`;

    const inner = document.createElement('div');
    inner.setAttribute('x-data', `{ open: ${expanded} }`);
    inner.setAttribute('x-cloak', '');
    inner.className = 'accordion-support-inner';

    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      'accordion-support-trigger w-full flex items-center py-4 font-heading heading-secondary cursor-pointer text-left text-scheme-text border-scheme-text border-t-2';
    button.setAttribute('@click', 'open = !open');
    button.setAttribute(':aria-expanded', 'open');
    button.setAttribute('aria-controls', panelId);
    button.innerHTML = `<span class="break-words flex-1">${escapeHtml(label)}</span>${CHEVRON_SVG}`;

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'accordion-support-panel';
    panel.setAttribute('x-show', 'open');
    panel.setAttribute('x-transition:enter', 'transition ease-out duration-200');
    panel.setAttribute('x-transition:enter-start', 'opacity-0');
    panel.setAttribute('x-transition:enter-end', 'opacity-100');
    panel.setAttribute('x-transition:leave', 'transition ease-in duration-150');
    panel.setAttribute('x-transition:leave-start', 'opacity-100');
    panel.setAttribute('x-transition:leave-end', 'opacity-0');
    panel.setAttribute('role', 'region');

    while (el.firstChild) panel.appendChild(el.firstChild);
    inner.appendChild(button);
    inner.appendChild(panel);
    el.appendChild(inner);

    if (typeof Alpine !== 'undefined' && Alpine.initTree) {
      Alpine.initTree(el);
    }
  });
}

function init() {
  runStartEndAccordions();
  runAccordionSupportClass();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', (e) => {
  if (e.target && e.target.nodeType === Node.ELEMENT_NODE) {
    runStartEndAccordions(e.target);
    runAccordionSupportClass(e.target);
    const main = document.getElementById('MainContent');
    if (main && !e.target.contains(main)) {
      runStartEndAccordions(main);
      runAccordionSupportClass(main);
    }
  } else {
    init();
  }
});
